/**
 * Cross-subdomain theme handoff between marketing (e.g. courtsync.io) and the web app (e.g. app.courtsync.io).
 * localStorage is per-origin; a first-party cookie on `.courtsync.io` is readable on both hosts.
 * On localhost, cookies omit Domain and are still shared across ports for the same host.
 */

export const COURTSYNC_THEME_COOKIE_NAME = 'courtsync-theme' as const;

export type CourtSyncThemeMode = 'light' | 'dark' | 'system';

export function isCourtSyncThemeMode(value: string | null | undefined): value is CourtSyncThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** Parse `document.cookie` for the CourtSync theme value. */
export function parseThemeCookie(cookieString: string): CourtSyncThemeMode | null {
  const prefix = `${COURTSYNC_THEME_COOKIE_NAME}=`;
  const segments = cookieString.split(';');
  for (const segment of segments) {
    const part = segment.trim();
    if (!part.startsWith(prefix)) continue;
    const raw = part.slice(prefix.length);
    let decoded: string;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      decoded = raw;
    }
    if (isCourtSyncThemeMode(decoded)) return decoded;
  }
  return null;
}

/** Domain attribute for the theme cookie; omit on localhost so the cookie is host-only but still shared across ports. */
export function getThemeCookieDomain(hostname: string): string | undefined {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return undefined;
  if (hostname === 'courtsync.io' || hostname.endsWith('.courtsync.io')) return '.courtsync.io';
  return undefined;
}

/** Minimal browser globals for theme cookie (avoids requiring DOM lib in React Native/mobile typecheck). */
type GlobalWithDocumentCookie = typeof globalThis & {
  document?: { cookie: string };
  location?: { hostname: string; protocol: string };
};

/**
 * Persist the user's theme choice for other CourtSync origins (landing ↔ web app).
 * Safe to call from client components only.
 */
export function setCrossSiteThemeCookie(theme: CourtSyncThemeMode): void {
  const g = globalThis as GlobalWithDocumentCookie;
  if (g.document === undefined) return;

  const hostname = g.location?.hostname ?? '';
  const domain = getThemeCookieDomain(hostname);
  const maxAge = 60 * 60 * 24 * 365; // 1 year
  const useSecure = (g.location?.protocol ?? '') === 'https:' && domain !== undefined;

  let cookie = `${COURTSYNC_THEME_COOKIE_NAME}=${encodeURIComponent(theme)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
  if (domain) cookie += `; Domain=${domain}`;
  if (useSecure) cookie += `; Secure`;
  g.document.cookie = cookie;
}
